import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import {
  addImageToMap,
  addReferenceLayers,
  forceMapResize,
} from '../../utils/map';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import MapContainer from '../map/MapContainer';
import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import {
  MAP_INIT_POSITION,
  MAIN_SOURCE_ID,
  POINT_SOURCE_ID,
  ZOOM_THRESHOLD,
} from './constants';
import { emptyFeature, emptyGeojson } from '../../utils/geojson';
import { NAIP_LAYER_ID } from '../map/NaipLayer';
import { getRootUrl } from '../../utils/utils';
import { getCenterPoint } from './helpers';
import InfoCard from './InfoCard';
import { BOTTOM_LAYER_ABOVE_SATELLITE } from '../../constants/map';
import PositionedOverMap from '../basics/PositionedOverMap';

const LoadingCard = styled.div`
  color: #212529;
  background-color: #fff;
  border-color: var(--border-color);
  border-radius: 3px;

  padding: 16px;
  font-size: 16px;
  box-shadow: 2px 2px 4px 2px rgb(0 0 0 / 20%);
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
`;

const SELECTED_SOURCE_ID = 'pe-selected-source';
const SELECTED_POINT_SOURCE_ID = 'pe-selected-point-source';

const MAIN_FILL_LAYER_ID = 'pe-main-fill-layer';
const MAIN_LINE_LAYER_ID = 'pe-main-line-layer';
const POINT_LAYER_ID = 'pe-point-layer';
const INTERACTION_LAYER_ID = 'pe-interaction-layer';
const INTERACTION_POINT_LAYER_ID = 'pe-interaction-point-layer';
const SELECTED_FILL_LAYER_ID = 'pe-selected-fill-layer';
const SELECTED_LINE_LAYER_ID = 'pe-selected-line-layer';
const SELECTED_POINT_LAYER_ID = 'pe-selected-point-layer';

const NAIP_SOURCE_ID = 'mapbox-naip';

const ICON_LAYOUT = {
  'icon-size': 0.4,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
};

// These are necessary to prevent some nasty mapbox bugs
// See https://github.com/mapbox/mapbox-gl-js/issues/7023
const SOURCE_SETTINGS_TO_AVOID_FILL_BUGS = {
  // Setting tolerance to 0 reduces bugs related to coordinate conversions
  tolerance: 0,
  // Setting buffer to its max value and maxzoom to its min value reduces bugs related to tile cutoff when zooming
  buffer: 512,
  maxzoom: 12,
  // Setting promoteId to 'id' helps mapbox de-dup features with the same id
  promoteId: 'id',
};

const onLoad = (mapRef) => {
  const map = mapRef.current;
  addReferenceLayers(map);

  // ADD IMAGES
  const rootUrl = getRootUrl();
  addImageToMap(map, 'blue-pin', `${rootUrl}/images/BluePin.png`);
  addImageToMap(map, 'yellow-pin', `${rootUrl}/images/YellowPin.png`);

  // Add NAIP
  map.addSource(NAIP_SOURCE_ID, {
    type: 'raster',
    url: 'mapbox://mapbox.naip',
    minZoom: 12,
    maxZoom: 18,
  });
  map.addLayer(
    {
      id: NAIP_LAYER_ID,
      source: NAIP_SOURCE_ID,
      type: 'raster',
      minZoom: 12,
    },
    BOTTOM_LAYER_ABOVE_SATELLITE,
  );

  // Ensure that the sources for the fill layers have the settings that avoid bugs related to polygon fill
  const mainSourceData = emptyGeojson(SOURCE_SETTINGS_TO_AVOID_FILL_BUGS);
  const selectedSourceData = emptyGeojson(SOURCE_SETTINGS_TO_AVOID_FILL_BUGS);
  const pointSourceData = emptyGeojson();
  const selectedPointSourceData = emptyGeojson();

  // Convenience for hot reloading -- keep the data around
  if (import.meta.hot && window.PROPAGATE_TEMP_STORAGE) {
    const data = window.PROPAGATE_TEMP_STORAGE;
    if (data.MAIN_SOURCE_ID) mainSourceData.data = data.MAIN_SOURCE_ID;
    if (data.POINT_SOURCE_ID) pointSourceData.data = data.POINT_SOURCE_ID;
    delete window.PROPAGATE_TEMP_STORAGE;
  }

  // Source for all features (for zoomed in view)
  map.addSource(MAIN_SOURCE_ID, mainSourceData);

  // Source for 1 point per feature (for zoomed out view)
  map.addSource(POINT_SOURCE_ID, pointSourceData);

  // Source for just selected feature
  map.addSource(SELECTED_SOURCE_ID, selectedSourceData);

  // Source for just selected feature (point)
  map.addSource(SELECTED_POINT_SOURCE_ID, selectedPointSourceData);

  // Main layer for all features (fill)
  map.addLayer({
    id: MAIN_FILL_LAYER_ID,
    minzoom: ZOOM_THRESHOLD,
    type: 'fill',
    source: MAIN_SOURCE_ID,
    paint: {
      'fill-color': 'white',
      'fill-opacity': 0.5,
    },
  });

  // Main layer for all features (line)
  map.addLayer({
    id: MAIN_LINE_LAYER_ID,
    minzoom: ZOOM_THRESHOLD,
    type: 'line',
    source: MAIN_SOURCE_ID,
    paint: {
      'line-color': 'white',
      'line-width': 3,
    },
  });

  // Layer to show 1 point per feature when zoomed out
  map.addLayer({
    id: POINT_LAYER_ID,
    maxzoom: ZOOM_THRESHOLD,
    type: 'symbol',
    source: POINT_SOURCE_ID,
    layout: {
      'icon-image': 'blue-pin',
      ...ICON_LAYOUT,
    },
  });

  // Layer for selected feature (fill)
  map.addLayer({
    id: SELECTED_FILL_LAYER_ID,
    minzoom: ZOOM_THRESHOLD,
    type: 'fill',
    source: SELECTED_SOURCE_ID,
    paint: {
      'fill-color': 'aqua',
      'fill-opacity': 0.5,
    },
  });

  // Layer for selected feature (line)
  map.addLayer({
    id: SELECTED_LINE_LAYER_ID,
    minzoom: ZOOM_THRESHOLD,
    type: 'line',
    source: SELECTED_SOURCE_ID,
    paint: {
      'line-color': 'aqua',
      'line-width': 3,
    },
  });

  // Layer for selected feature (point)
  map.addLayer({
    id: SELECTED_POINT_LAYER_ID,
    maxzoom: ZOOM_THRESHOLD,
    type: 'symbol',
    source: SELECTED_POINT_SOURCE_ID,
    layout: {
      'icon-image': 'yellow-pin',
      ...ICON_LAYOUT,
    },
  });

  // Layer for just interactions
  map.addLayer({
    id: INTERACTION_LAYER_ID,
    minzoom: ZOOM_THRESHOLD,
    type: 'fill',
    source: MAIN_SOURCE_ID,
    paint: {
      // Don't show the fill, this layer is just for hover/click events.
      'fill-opacity': 0,
    },
  });

  // Layer for just interactions (point)
  map.addLayer({
    id: INTERACTION_POINT_LAYER_ID,
    maxzoom: ZOOM_THRESHOLD,
    type: 'symbol',
    source: POINT_SOURCE_ID,
    layout: {
      'icon-image': 'blue-pin',
      ...ICON_LAYOUT,
    },
    paint: {
      // Don't show the icon, this layer is just for hover/click events.
      'icon-opacity': 0,
    },
  });
};

const onUnload = (mapRef) => {
  const map = mapRef.current;

  // Convenience for hot reloading -- keep the data around
  if (import.meta.hot) {
    window.PROPAGATE_TEMP_STORAGE = {
      MAIN_SOURCE_ID: map.getSource(MAIN_SOURCE_ID)?._data, // eslint-disable-line no-underscore-dangle
      POINT_SOURCE_ID: map.getSource(MAIN_SOURCE_ID)?._data, // eslint-disable-line no-underscore-dangle
    };
  }

  // Our layers
  map.removeLayer(MAIN_FILL_LAYER_ID);
  map.removeLayer(MAIN_LINE_LAYER_ID);
  map.removeLayer(POINT_LAYER_ID);
  map.removeLayer(INTERACTION_LAYER_ID);
  map.removeLayer(INTERACTION_POINT_LAYER_ID);
  map.removeLayer(SELECTED_FILL_LAYER_ID);
  map.removeLayer(SELECTED_LINE_LAYER_ID);
  map.removeLayer(SELECTED_POINT_LAYER_ID);

  // Our sources
  map.removeSource(MAIN_SOURCE_ID);
  map.removeSource(POINT_SOURCE_ID);
  map.removeSource(SELECTED_SOURCE_ID);
  map.removeSource(SELECTED_POINT_SOURCE_ID);

  // NAIP
  map.removeLayer(NAIP_LAYER_ID);
  map.removeSource(NAIP_SOURCE_ID);
};

const PortfolioEvaluatorMap = () => {
  const mapContainer = useRef(null);

  const {
    mapRef,
    mapLoaded,
    map,
    tilesets,
    features,
    suitabilityData,
    setMapLoaded,
    hoveredFeatureId,
    setHoveredFeatureId,
    selectedFeatureId,
    setSelectedFeatureId,
    loadingState,
  } = usePortfolioEvaluatorContext();

  useEffect(forceMapResize, []);

  // Initialize the map
  useInitializeMapEffect({
    mapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    onLoad,
    onUnload,
    ...MAP_INIT_POSITION,
  });

  // Show selected feature on map
  useEffect(() => {
    if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

    const selectedFeature = features.find(
      (f) => f.properties.id === hoveredFeatureId,
    );

    const selectedFeatureSource = map.getSource(SELECTED_SOURCE_ID);
    if (selectedFeatureSource) {
      selectedFeatureSource.setData(selectedFeature ?? emptyFeature());
    }

    const selectedPointSource = map.getSource(SELECTED_POINT_SOURCE_ID);
    if (selectedPointSource) {
      const selectedPoint = selectedFeature
        ? getCenterPoint(selectedFeature)
        : emptyFeature();
      selectedPointSource.setData(selectedPoint);
    }
  }, [map, hoveredFeatureId, features]);

  // Highlight hovered feature
  useEffect(() => {
    if (!map || map._removed) return null; // eslint-disable-line no-underscore-dangle

    const onMouseMove = (e) => {
      const [feature] = map.queryRenderedFeatures(e.point, {
        layers: [INTERACTION_LAYER_ID, INTERACTION_POINT_LAYER_ID],
      });
      const id = feature?.properties?.id;
      if (id) {
        setHoveredFeatureId(id);
      } else {
        setHoveredFeatureId(null);
      }
    };

    const onMouseLeave = () => {
      setHoveredFeatureId(null);
    };

    const onClick = (e) => {
      const { id } = e.features[0].properties;
      if (id) {
        setSelectedFeatureId(id);
      } else {
        setSelectedFeatureId(null);
      }
    };

    map.on('mousemove', onMouseMove);
    map.on('mouseleave', onMouseLeave);
    map.on('mouseout', onMouseLeave);
    map.on('click', INTERACTION_LAYER_ID, onClick);
    map.on('click', INTERACTION_POINT_LAYER_ID, onClick);

    return () => {
      map.off('mousemove', onMouseMove);
      map.off('mouseleave', onMouseLeave);
      map.off('mouseout', onMouseLeave);
      map.off('click', INTERACTION_LAYER_ID, onClick);
      map.off('click', INTERACTION_POINT_LAYER_ID, onClick);
    };
  }, [
    map,
    hoveredFeatureId,
    features,
    setHoveredFeatureId,
    setSelectedFeatureId,
  ]);

  const selectedFeatureData = useMemo(() => {
    const i = features.findIndex((f) => f.properties.id === selectedFeatureId);
    if (i === -1) return null;

    const feature = features[i];
    const suitability = suitabilityData?.[i];
    return {
      ...feature,
      properties: {
        ...feature.properties,
        suitability,
      },
    };
  }, [selectedFeatureId, features, suitabilityData]);
  const lngLat = selectedFeatureData?.geometry.coordinates[0][0];

  return (
    <div className="w-100">
      {loadingState && (
        <>
          <Backdrop />
          <PositionedOverMap placement="bottom middle" zIndex={2010}>
            <LoadingCard className="px-4">
              <div>
                {loadingState.status === 'preloading'
                  ? 'Loading suitability tiles'
                  : 'Calculating suitability'}{' '}
                for {features.length} parcels across {tilesets.length} crops...
              </div>

              <div className="mt-2">
                {loadingState.progress[0]} / {loadingState.progress[1]}
              </div>
              <div className="mt-2">
                <progress
                  max="100"
                  value={
                    (loadingState.progress[0] / loadingState.progress[1]) * 100
                  }
                />
              </div>
            </LoadingCard>
          </PositionedOverMap>
        </>
      )}

      <MapContainer ref={mapContainer} className="map-container h-100 w-100" />
      {selectedFeatureData && lngLat && (
        <InfoCard
          mapRef={mapRef}
          lngLat={lngLat}
          featureData={selectedFeatureData}
          onClose={() => setSelectedFeatureId(null)}
        />
      )}
    </div>
  );
};

export default PortfolioEvaluatorMap;
